import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UseFormReturn, useFormState, useWatch } from 'react-hook-form';
import { IDocumentData } from '@models/document/IDocumentData';
import { ITab } from '@srm/ui/types/ITab';
import type { IDocumentTab } from './tabs/DocumentTab.types';
import { errorFieldMatchesTab, getDocumentErrorAnchors } from './useFirstInvalidTabRequest';
import { FormulaManager, hasRemoteFormulaData, hasUnsupportedRemoteFormulaData } from '@utils/managers/FormulaManager';
import { DocumentRuntimeContext } from '@/context/DocumentRuntimeContext';
import { EnsureExternalDataSourceLoaded, extractExternalDataSourceNames } from '@/utils/externalDataSourceUtils';
import { type IFormulaTraceTarget, useDocumentFormulaTraceRegistry } from '@/context/DocumentFormulaTraceContext';
import { evaluateNameFormula } from '@utils/nameFormula';

const hasVisibilityRules = (rules?: string | null): rules is string =>
    rules !== undefined && rules !== null && rules !== '';

/** Адрес страницы ведёт на вкладку — то же сопоставление, что у Tabs при выборе вкладки по URL. */
const locationTargetsTab = (tab: ITab) => {
    const url = tab.url;
    if (!url) return false;
    const { hash, pathname } = window.location;
    if (url.startsWith('#')) return tab.exact ? url === hash : hash.includes(url);
    return tab.exact ? url === pathname : pathname.includes(url);
};

export const getDocumentTabInvalid = (
    tab: ITab,
    errorFieldIds: number[],
    isEdit: boolean,
    isNew: boolean,
    documentTabIds: Record<number, string> = {},
) => {
    const docTab = tab as Partial<IDocumentTab>;
    if ((!isEdit && !isNew) || !docTab.includedFields?.length || !errorFieldIds.length) {
        return false;
    }

    return errorFieldIds.some(
        (fieldId) =>
            docTab.includedFields!.includes(fieldId) && errorFieldMatchesTab(fieldId, tab.value, documentTabIds),
    );
};

interface IUseDocumentVisibleTabsProps {
    tabs?: ITab[];
    methods: UseFormReturn<IDocumentData>;
    isEdit: boolean;
    isNew: boolean;
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

/**
 * Видимые вкладки и признак `settled`: набор посчитан для текущего режима и текущих вкладок. Правила
 * видимости считаются асинхронно, и сразу после смены режима или данных хук ещё отдаёт прошлый набор — по `settled`
 * смена режима карточки понимает, что форма доехала до нового состояния.
 */
export const useDocumentVisibleTabs = ({
    tabs,
    methods,
    isEdit,
    isNew,
    ensureExternalDataSourceLoaded,
}: IUseDocumentVisibleTabsProps) => {
    const allTabs = useMemo(() => tabs ?? [], [tabs]);
    const runtime = useContext(DocumentRuntimeContext);
    const traceRegistry = useDocumentFormulaTraceRegistry();
    // Только `errors`: чтение `isValid` включает в RHF расчёт валидности формы на каждую регистрацию поля и каждое
    // изменение — с resolver это полный прогон по всем смонтированным полям на каждое новое поле (переход в правку,
    // первое открытие вкладки).
    const { errors } = useFormState({
        control: methods.control,
        disabled: !isEdit && !isNew,
    });

    const visibilityEntries = useMemo(
        () =>
            allTabs.map((tab, index) => {
                const docTab = tab as Partial<IDocumentTab>;
                const visibilityRules = docTab.visibilityRules;
                const traceTarget: IFormulaTraceTarget = {
                    targetId: tab.value ?? `index-${index}`,
                    targetKind: 'tab',
                    targetName: typeof tab.label === 'string' ? tab.label : (tab.value ?? `Вкладка ${index + 1}`),
                    ruleKind: 'visibility',
                };
                if (!hasVisibilityRules(visibilityRules)) {
                    return {
                        index,
                        visibilityRules: undefined,
                        manager: undefined,
                        watchFields: [] as string[],
                        externalSourceNames: [] as string[],
                        fallbackReason: undefined,
                        traceTarget,
                    };
                }

                const externalSourceNames = !isNew ? extractExternalDataSourceNames(visibilityRules) : [];
                if (!docTab.fields) {
                    return {
                        index,
                        visibilityRules,
                        manager: undefined,
                        watchFields: [] as string[],
                        externalSourceNames,
                        fallbackReason: 'Контекст полей вкладки недоступен.',
                        traceTarget,
                    };
                }
                if (
                    runtime.mode === 'supportPreview' &&
                    hasUnsupportedRemoteFormulaData(visibilityRules, runtime.dataSource)
                ) {
                    return {
                        index,
                        visibilityRules,
                        manager: undefined,
                        watchFields: [] as string[],
                        externalSourceNames,
                        fallbackReason: 'Session-scoped источник для серверной функции формулы не настроен.',
                        traceTarget,
                    };
                }

                const manager = new FormulaManager(visibilityRules, runtime.dataSource, runtime.requestCache);
                manager.Init(docTab.fields, docTab.formMethods);

                return {
                    index,
                    visibilityRules,
                    manager,
                    watchFields: manager.GetWatchFields(),
                    externalSourceNames,
                    fallbackReason: undefined,
                    traceTarget,
                };
            }),
        [allTabs, isNew, runtime.mode, runtime.dataSource, runtime.requestCache],
    );

    // Заголовок вкладки — блок-страница со своим `nameFormula`. Держим отдельным набором
    // менеджеров: правило видимости и формула имени независимы, и вкладка может иметь только
    // одно из двух.
    const nameEntries = useMemo(
        () =>
            allTabs.map((tab, index) => {
                const docTab = tab as Partial<IDocumentTab>;
                const nameFormula = docTab.nameFormula;
                const traceTarget: IFormulaTraceTarget = {
                    targetId: tab.value ?? `index-${index}`,
                    targetKind: 'tab',
                    targetName: typeof tab.label === 'string' ? tab.label : (tab.value ?? `Вкладка ${index + 1}`),
                    ruleKind: 'formula',
                };
                const empty = {
                    index,
                    nameFormula: undefined as string | undefined,
                    evalNameFormula: false,
                    manager: undefined as FormulaManager | undefined,
                    watchFields: [] as string[],
                    externalSourceNames: [] as string[],
                    fallbackReason: undefined as string | undefined,
                    traceTarget,
                };

                if (!nameFormula || !docTab.fields) {
                    return empty;
                }

                const externalSourceNames = !isNew ? extractExternalDataSourceNames(nameFormula) : [];
                if (
                    runtime.mode === 'supportPreview' &&
                    hasUnsupportedRemoteFormulaData(nameFormula, runtime.dataSource)
                ) {
                    return {
                        ...empty,
                        nameFormula,
                        externalSourceNames,
                        fallbackReason: 'Session-scoped источник для серверной функции формулы не настроен.',
                    };
                }

                const manager = new FormulaManager(nameFormula, runtime.dataSource, runtime.requestCache);
                manager.Init(docTab.fields, docTab.formMethods);

                return {
                    index,
                    nameFormula,
                    evalNameFormula: docTab.evalNameFormula === true,
                    manager,
                    watchFields: manager.GetWatchFields(),
                    externalSourceNames,
                    fallbackReason: undefined,
                    traceTarget,
                };
            }),
        [allTabs, isNew, runtime.mode, runtime.dataSource, runtime.requestCache],
    );

    // Селективный пересчёт по смене режима (см. useFormulaState): режимно-независимые правила
    // видимости вкладок не требуют полного async-прохода (в т.ч. GETLINKSCOUNT-формул) на каждом
    // view↔edit. Признак даёт разбор формулы (FormulaManager.DependsOnMode) — правило может
    // зависеть от режима и без is-токенов. Актуальный режим для пересчётов по другим причинам
    // эффект читает из ref.
    const modeDependent = useMemo(
        () =>
            visibilityEntries.some((entry) => entry.manager?.DependsOnMode() === true) ||
            nameEntries.some((entry) => entry.manager?.DependsOnMode() === true),
        [visibilityEntries, nameEntries],
    );
    const modeRef = useRef({ isEdit, isNew });
    modeRef.current = { isEdit, isNew };
    const modeKey = modeDependent ? `${isEdit}:${isNew}` : 'static';

    const watchFieldNames = useMemo(
        () =>
            Array.from(
                new Set([
                    ...visibilityEntries.flatMap((entry) => entry.watchFields),
                    ...nameEntries.flatMap((entry) => entry.watchFields),
                ]),
            ),
        [visibilityEntries, nameEntries],
    );

    const watchValues = useWatch({
        control: methods.control,
        name: watchFieldNames as never,
    });

    // source — набор правил, по которому посчитан результат: после сохранения меняются данные,
    // а режимно-независимый modeKey остаётся прежним.
    // partial — первый показ без вкладок с сетевыми правилами: они ещё считаются.
    const [evaluated, setEvaluated] = useState<{
        tabs: ITab[];
        modeKey: string;
        source: typeof visibilityEntries;
        partial?: boolean;
    }>();
    const visibleTabs = evaluated?.tabs;

    useEffect(
        () => () => {
            if (!traceRegistry) return;
            visibilityEntries.forEach((entry) => traceRegistry.remove(entry.traceTarget));
            nameEntries.forEach((entry) => traceRegistry.remove(entry.traceTarget));
        },
        [traceRegistry, visibilityEntries, nameEntries],
    );

    useEffect(() => {
        let canceled = false;

        const evaluateVisibility = async () => {
            const values = Array.isArray(watchValues) ? watchValues : [watchValues];
            const valuesByName = new Map<string, unknown>();
            watchFieldNames.forEach((name, index) => {
                valuesByName.set(name, values[index]);
            });

            // Вкладка с видимостью и именем либо null — скрыта.
            const resolveTab = async (entry: (typeof visibilityEntries)[number], tab: ITab): Promise<ITab | null> => {
                let visible = true;
                if (!entry.visibilityRules) {
                    traceRegistry?.remove(entry.traceTarget);
                } else if (entry.fallbackReason) {
                    traceRegistry?.upsert({
                        ...entry.traceTarget,
                        source: entry.visibilityRules,
                        result: true,
                        dependencies: [],
                        externalSources: entry.externalSourceNames,
                        status: 'fallback',
                        error: entry.fallbackReason,
                    });
                } else if (entry.manager) {
                    try {
                        let externalDataLoaded = false;
                        if (!isNew && ensureExternalDataSourceLoaded && entry.externalSourceNames.length > 0) {
                            await Promise.all(
                                entry.externalSourceNames.map((name) => ensureExternalDataSourceLoaded(name)),
                            );
                            externalDataLoaded = true;
                        }

                        const docTab = tab as Partial<IDocumentTab>;
                        if (externalDataLoaded && docTab.fields) {
                            entry.manager.Init(docTab.fields, docTab.formMethods);
                        }

                        const entryValues = entry.watchFields.map((fieldName) => valuesByName.get(fieldName));
                        const valuesArg =
                            entryValues.length > 0 && !entryValues.every((value) => value === undefined)
                                ? entryValues
                                : undefined;
                        visible = !!(await entry.manager.EvalFormulaValues(
                            modeRef.current.isEdit,
                            modeRef.current.isNew,
                            valuesArg,
                        ));
                        traceRegistry?.upsert({
                            ...entry.traceTarget,
                            source: entry.visibilityRules,
                            preparedExpression: entry.manager.GetPreparedExpression(),
                            result: visible,
                            dependencies: entry.manager.GetFields().map((field, index) => ({
                                field,
                                value: entryValues[index] ?? docTab.fields?.[field]?.value,
                            })),
                            externalSources: entry.externalSourceNames,
                            status: 'evaluated',
                        });
                    } catch (error: unknown) {
                        traceRegistry?.upsert({
                            ...entry.traceTarget,
                            source: entry.visibilityRules,
                            preparedExpression: entry.manager.GetPreparedExpression(),
                            result: undefined,
                            dependencies: entry.manager.GetFields().map((field) => ({
                                field,
                                value: (tab as Partial<IDocumentTab>).fields?.[field]?.value,
                            })),
                            externalSources: entry.externalSourceNames,
                            status: 'error',
                            error: error instanceof Error ? error.message : String(error),
                        });
                        throw error;
                    }
                }

                if (!visible) {
                    return null;
                }

                // Заголовок считаем только у видимых вкладок и никогда не роняем им расчёт:
                // ошибка формулы имени оставляет статический label (в отличие от правила
                // видимости, где ошибка — это ошибка схемы и её видно по упавшей вкладке).
                const nameEntry = nameEntries[entry.index];
                let label = tab.label;
                if (nameEntry?.fallbackReason) {
                    traceRegistry?.upsert({
                        ...nameEntry.traceTarget,
                        source: nameEntry.nameFormula!,
                        result: label,
                        dependencies: [],
                        externalSources: nameEntry.externalSourceNames,
                        status: 'fallback',
                        error: nameEntry.fallbackReason,
                    });
                } else if (nameEntry?.manager) {
                    const docTab = tab as Partial<IDocumentTab>;
                    const nameValues = nameEntry.watchFields.map((fieldName) => valuesByName.get(fieldName));
                    try {
                        if (!isNew && ensureExternalDataSourceLoaded && nameEntry.externalSourceNames.length > 0) {
                            await Promise.all(
                                nameEntry.externalSourceNames.map((name) => ensureExternalDataSourceLoaded(name)),
                            );
                            if (docTab.fields) {
                                nameEntry.manager.Init(docTab.fields, docTab.formMethods);
                            }
                        }

                        const valuesArg =
                            nameValues.length > 0 && !nameValues.every((value) => value === undefined)
                                ? nameValues
                                : undefined;
                        const computed = await evaluateNameFormula(
                            nameEntry.manager,
                            modeRef.current.isEdit,
                            modeRef.current.isNew,
                            nameEntry.evalNameFormula,
                            valuesArg,
                        );
                        if (computed) {
                            label = computed;
                        }
                        traceRegistry?.upsert({
                            ...nameEntry.traceTarget,
                            source: nameEntry.nameFormula!,
                            preparedExpression: nameEntry.manager.GetPreparedExpression(),
                            result: computed,
                            dependencies: nameEntry.manager.GetFields().map((field, fieldIndex) => ({
                                field,
                                value: nameValues[fieldIndex] ?? docTab.fields?.[field]?.value,
                            })),
                            externalSources: nameEntry.externalSourceNames,
                            status: 'evaluated',
                        });
                    } catch (error: unknown) {
                        traceRegistry?.upsert({
                            ...nameEntry.traceTarget,
                            source: nameEntry.nameFormula!,
                            preparedExpression: nameEntry.manager.GetPreparedExpression(),
                            result: undefined,
                            dependencies: nameEntry.manager.GetFields().map((field, fieldIndex) => ({
                                field,
                                value: nameValues[fieldIndex] ?? docTab.fields?.[field]?.value,
                            })),
                            externalSources: nameEntry.externalSourceNames,
                            status: 'error',
                            error: error instanceof Error ? error.message : String(error),
                        });
                        console.error(error);
                    }
                } else if (nameEntry) {
                    traceRegistry?.remove(nameEntry.traceTarget);
                }

                return { ...tab, active: false, label };
            };

            const assemble = (resolved: (ITab | null)[]) => {
                let firstActiveFound = false;
                return resolved.flatMap((resolvedTab) => {
                    if (!resolvedTab) return [];
                    if (firstActiveFound || resolvedTab.disabled) return [resolvedTab];
                    firstActiveFound = true;
                    return [{ ...resolvedTab, active: true }];
                });
            };

            // Правила вкладок считаются параллельно: сетевые (GETLINKSCOUNT, внешние источники) ждут
            // сервер, а полоса вкладок ждала бы самую медленную из них.
            const pending = visibilityEntries.flatMap((entry) => {
                const tab = allTabs[entry.index];
                if (!tab) return [];
                const nameEntry = nameEntries[entry.index];
                const remote =
                    (!!entry.manager &&
                        (entry.externalSourceNames.length > 0 || hasRemoteFormulaData(entry.visibilityRules))) ||
                    (!!nameEntry?.manager &&
                        (nameEntry.externalSourceNames.length > 0 || hasRemoteFormulaData(nameEntry.nameFormula)));
                return [{ tab, remote, result: resolveTab(entry, tab) }];
            });
            const all = Promise.allSettled(pending.map((item) => item.result));
            const tabsOf = (results: PromiseSettledResult<ITab | null>[]) =>
                results.map((result) => (result.status === 'fulfilled' ? result.value : null));

            // Первый показ не ждёт сети: форма открывается с вкладками по локальным правилам, сетевые
            // добавляются следом. Частичный набор не показываем, если он открыл бы не ту вкладку: адрес
            // ведёт на ещё не посчитанную (не найдя её, Tabs открыл бы первую и переписал адрес) или
            // сетевая стоит раньше первой видимой локальной (активной стала бы не первая вкладка).
            const remotePending = pending.filter((item) => item.remote);
            if (remotePending.length > 0 && !remotePending.some((item) => locationTargetsTab(item.tab))) {
                const local = await Promise.allSettled(
                    pending.map((item) => (item.remote ? Promise.resolve(null) : item.result)),
                );
                if (canceled) return;
                const localTabs = tabsOf(local);
                const firstLocal = localTabs.findIndex((tab) => tab && !tab.disabled);
                const opensFirstTab = firstLocal !== -1 && !pending.slice(0, firstLocal).some((item) => item.remote);
                if (opensFirstTab && local.every((result) => result.status === 'fulfilled')) {
                    setEvaluated(
                        (prev) =>
                            prev ?? { tabs: assemble(localTabs), modeKey, source: visibilityEntries, partial: true },
                    );
                }
            }

            const results = await all;
            if (canceled) return;
            const nextTabs = assemble(tabsOf(results));
            if (results.some((result) => result.status === 'rejected')) {
                // Упавшее правило оставляет прежний набор; на первом показе прежнего нет — вкладку
                // с ошибкой скрываем, остальные показываем, а не держим карточку на скелетоне.
                setEvaluated(
                    (prev) =>
                        (prev && { ...prev, modeKey, source: visibilityEntries, partial: false }) ?? {
                            tabs: nextTabs,
                            modeKey,
                            source: visibilityEntries,
                        },
                );
                return;
            }
            setEvaluated({ tabs: nextTabs, modeKey, source: visibilityEntries });
        };

        // Отказ вне правил вкладок оставляет прежний набор, но расчёт для этого режима окончен: иначе
        // смена режима ждала бы его до конца бюджета склейки.
        evaluateVisibility().catch(() => {
            if (!canceled)
                setEvaluated((prev) => prev && { ...prev, modeKey, source: visibilityEntries, partial: false });
        });

        return () => {
            canceled = true;
        };
    }, [
        allTabs,
        ensureExternalDataSourceLoaded,
        isNew,
        modeKey,
        nameEntries,
        traceRegistry,
        visibilityEntries,
        watchFieldNames,
        watchValues,
    ]);

    // Подсветку невалидных вкладок держим отдельно от async-расчёта видимости и
    // реактивимся на строку ключей ошибочных полей, а не на сам объект errors:
    // RHF мутирует errors на месте (ссылка стабильна), поэтому зависимость по
    // объекту не сработала бы и invalid замёрз бы на первом расчёте.
    const fieldErrors = (errors as { fields?: Record<string, unknown> }).fields;
    const errorFieldKeys = fieldErrors
        ? JSON.stringify(
              Object.entries(fieldErrors).map(([id, error]) => [
                  id,
                  (error as { value?: { documentTabId?: string } } | undefined)?.value?.documentTabId ?? '',
              ]),
          )
        : '';

    const errorTargets = useMemo(() => {
        const entries = errorFieldKeys ? (JSON.parse(errorFieldKeys) as [string, string][]) : [];
        const ids: number[] = [];
        const tabIds: Record<number, string> = {};
        entries.forEach(([rawId, tabId]) => {
            const id = Number(rawId);
            if (!Number.isInteger(id)) return;
            ids.push(id);
            if (tabId) tabIds[id] = tabId;
        });
        return { ids, tabIds };
    }, [errorFieldKeys]);

    // Блок вложений адресуется якорем, а не индексом поля: его ошибка лежит вне errors.fields,
    // а вкладка может вовсе не содержать полей. Реактивность — той же строкой-ключом.
    const anchorTabKeys = getDocumentErrorAnchors(errors)
        .map(({ documentTabId }) => documentTabId)
        .sort()
        .join('|');

    const settled = evaluated?.modeKey === modeKey && evaluated.source === visibilityEntries && !evaluated.partial;
    const markedTabs = useMemo(
        () =>
            visibleTabs?.map((tab) => {
                const invalid =
                    getDocumentTabInvalid(tab, errorTargets.ids, isEdit, isNew, errorTargets.tabIds) ||
                    ((isEdit || isNew) && !!tab.value && anchorTabKeys.split('|').includes(tab.value));
                return invalid ? { ...tab, invalid, title: 'В этом разделе есть ошибки валидации' } : tab;
            }),
        [visibleTabs, errorTargets, anchorTabKeys, isEdit, isNew],
    );

    return { tabs: markedTabs, settled };
};
